import { useVoteTransaction, useVoteValidation } from "@/hooks";
import { useCallback, useMemo } from "react";
import { Divider, Icon, Tooltip } from "../shared";
import type { FarmWeights } from "./types";
import {
    TransactionDialogContent,
    TransactionFeeSection,
    TransactionSection,
    TransactionDetailRow,
} from "../transaction";
import { IconButton } from "@mui/material";
import { ActionText } from "@/constants/text.enum";
import { useEpoch } from "@/hooks/infinifi/useEpoch";
import { safaryTrackVoteSuccess } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "../../hooks/shared/tracking-consent/useTrackingConsent";

export interface VoteSubmitDialogProps {
    liquidVotes: FarmWeights;
    illiquidVotes: FarmWeights;
    onClose: () => void;
}

export function VoteSubmitDialog({ liquidVotes, illiquidVotes, onClose }: VoteSubmitDialogProps) {
    const { currentEpoch } = useEpoch();
    const { address: walletAddress } = useAccount();
    const { noLockedPositions, alreadyVoted, unwindingEpochs } = useVoteValidation();

    const {
        gasPriceUSD,
        executeVoteTransaction,
        isExecuting,
        transformedLiquidVotes,
        transformedIlliquidVotes,
        gasError,
    } = useVoteTransaction({
        liquidVotes,
        illiquidVotes,
        unwindingEpochs,
    });

    const { canTrack } = useTrackingConsent();
    const handleSubmit = useCallback(async () => {
        try {
            if (!walletAddress) throw new Error("Wallet address is required for voting");
            if (noLockedPositions) throw new Error("No locked positions to vote with");
            if (alreadyVoted) throw new Error("User has already voted this epoch");
            if (!currentEpoch) throw new Error("Current epoch is not available");

            await executeVoteTransaction()
                .then(() => {
                    canTrack &&
                        safaryTrackVoteSuccess({
                            epoch: currentEpoch + 1, // vote is for next epoch
                            walletAddress,
                        });
                })
                .finally(() => onClose());
        } catch (e) {
            console.error(e);
        }
    }, [executeVoteTransaction, onClose, walletAddress, noLockedPositions, alreadyVoted, currentEpoch]);

    const highestPositionLabel = noLockedPositions ? "-" : `${unwindingEpochs} week`;

    const LOADING_TEXT = useMemo(() => {
        if (isExecuting) return ActionText.VOTING;
        return "";
    }, [isExecuting, gasError]);

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleSubmit}
            isLoading={isExecuting}
            loadingText={LOADING_TEXT}
            confirmText={noLockedPositions ? "No positions to vote on" : alreadyVoted ? "Already voted" : "Submit"}
            disabled={noLockedPositions || alreadyVoted || !!gasError || !currentEpoch || isExecuting}
            disclaimer="I acknowledge that by voting this week, I will be unable to start withdrawal until the next epoch."
        >
            <TransactionSection>
                <TransactionDetailRow label="Highest position" value={highestPositionLabel} />
                <TransactionDetailRow label="Liquid farms voted on" value={`${transformedLiquidVotes.length}`} />
                <TransactionDetailRow label="Illiquid farms voted on" value={`${transformedIlliquidVotes.length}`} />
                <Divider />
                <div className={`flex justify-between items-center variant='default'`}>
                    <span className="text-gray-400">
                        Locked position will be locked this week:
                        <Tooltip
                            title={
                                "Please note: if you vote this week, you cannot start exiting your longest locked position until next week."
                            }
                        >
                            <span className="inline-block cursor-help">
                                <IconButton size="small">
                                    <Icon name="Info" className="text-dark text-s w-4 h-4" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </span>
                </div>
                <Divider />
                <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
            </TransactionSection>
        </TransactionDialogContent>
    );
}
